import { Paper, Title, SimpleGrid, RingProgress, Text, Center, Stack, Loader, Tooltip } from '@mantine/core';
import { useClusterMetrics, useEndpointStatus, useServiceHealth } from './usePrometheus';

function formatBytes(bytes: number) {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(0)} MB`;
  return `${(bytes / 1e3).toFixed(0)} KB`;
}

function MetricRing({ label, tooltip, value, total, unit, color }: {
  label: string; tooltip: string; value: number; total: number; unit?: string; color: string;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Tooltip label={tooltip} multiline w={280} withArrow>
      <Center>
        <Stack align="center" gap={4}>
          <RingProgress
            size={100}
            thickness={10}
            roundCaps
            sections={[{ value: pct, color }]}
            label={<Text ta="center" fw={600} size="sm">{pct}%</Text>}
          />
          <Text size="sm" fw={500}>{label}</Text>
          <Text size="xs" c="dimmed">
            {unit === 'bytes' ? `${formatBytes(value)} / ${formatBytes(total)}` : `${value.toFixed(1)} / ${total}`}
          </Text>
        </Stack>
      </Center>
    </Tooltip>
  );
}

function formatDomain(url: string) {
  try { return new URL(url).hostname; } catch { return url; }
}

export function ClusterMetricsPanel() {
  const { data, isLoading } = useClusterMetrics();
  const { data: endpoints } = useEndpointStatus();
  const { data: services } = useServiceHealth();

  if (isLoading || !data) {
    return (
      <Paper shadow="xs" p="md" withBorder>
        <Center><Loader size="sm" /></Center>
      </Paper>
    );
  }

  const upCount = endpoints?.filter((e) => e.up).length ?? 0;
  const totalEndpoints = endpoints?.length ?? 0;
  const downEndpoints = endpoints?.filter((e) => !e.up) ?? [];
  const endpointTooltip = totalEndpoints === 0
    ? 'Keine Endpoint-Daten verfügbar.'
    : downEndpoints.length === 0
      ? `Alle ${totalEndpoints} Endpoints erreichbar:\n${endpoints!.map((e) => `  ${formatDomain(e.instance)}`).join('\n')}`
      : `${downEndpoints.length} von ${totalEndpoints} Endpoints nicht erreichbar:\n${endpoints!.map((e) => `  ${e.up ? '✅' : '❌'} ${formatDomain(e.instance)}`).join('\n')}`;

  const healthyServices = services?.filter((s) => s.up).length ?? 0;
  const totalServices = services?.length ?? 0;
  const downServices = services?.filter((s) => !s.up) ?? [];
  const serviceTooltip = totalServices === 0
    ? 'Keine Service-Daten verfügbar.'
    : downServices.length === 0
      ? `Alle ${totalServices} Services healthy:\n${services!.map((s) => `  ${s.name}`).join('\n')}`
      : `${downServices.length} von ${totalServices} Services nicht healthy:\n${services!.map((s) => `  ${s.up ? '✅' : '❌'} ${s.name}`).join('\n')}`;

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Title order={4} mb="md">Cluster-Auslastung</Title>
      <SimpleGrid cols={{ base: 2, sm: 4, md: 8 }}>
        <Tooltip label={endpointTooltip} multiline w={280} withArrow style={{ whiteSpace: 'pre-line' }}>
          <Center>
            <Stack align="center" gap={4}>
              <RingProgress
                size={100}
                thickness={10}
                roundCaps
                sections={[{ value: totalEndpoints > 0 ? Math.round((upCount / totalEndpoints) * 100) : 0, color: downEndpoints.length > 0 ? 'red' : 'green' }]}
                label={<Text ta="center" fw={600} size="sm">{upCount}/{totalEndpoints}</Text>}
              />
              <Text size="sm" fw={500}>Endpoints</Text>
              <Text size="xs" c="dimmed">{downEndpoints.length > 0 ? `${downEndpoints.length} down` : 'Alle OK'}</Text>
            </Stack>
          </Center>
        </Tooltip>
        <Tooltip label={serviceTooltip} multiline w={280} withArrow style={{ whiteSpace: 'pre-line' }}>
          <Center>
            <Stack align="center" gap={4}>
              <RingProgress
                size={100}
                thickness={10}
                roundCaps
                sections={[{ value: totalServices > 0 ? Math.round((healthyServices / totalServices) * 100) : 0, color: downServices.length > 0 ? 'red' : 'green' }]}
                label={<Text ta="center" fw={600} size="sm">{healthyServices}/{totalServices}</Text>}
              />
              <Text size="sm" fw={500}>Services</Text>
              <Text size="xs" c="dimmed">{downServices.length > 0 ? `${downServices.length} unhealthy` : 'Alle OK'}</Text>
            </Stack>
          </Center>
        </Tooltip>
        <MetricRing
          label="CPU"
          tooltip="CPU-Auslastung aller EKS Worker Nodes. Summe der aktiven CPU-Zeit (ohne idle) im Verhältnis zur Gesamtkapazität aller Kerne im Cluster."
          value={data.cpuUsage}
          total={data.cpuTotal}
          unit="cores"
          color="blue"
        />
        <MetricRing
          label="Memory"
          tooltip="Arbeitsspeicher-Nutzung aller EKS Worker Nodes. Zeigt den belegten RAM (MemTotal minus MemAvailable) im Verhältnis zum insgesamt verfügbaren Speicher."
          value={data.memoryUsage}
          total={data.memoryTotal}
          unit="bytes"
          color="grape"
        />
        <MetricRing
          label="Node Disk"
          tooltip="Root-Filesystem der EKS Worker Nodes (mountpoint /). Beinhaltet OS, Container-Images und Logs — nicht die Datenbank-Volumes."
          value={data.diskUsage}
          total={data.diskTotal}
          unit="bytes"
          color="orange"
        />
        <MetricRing
          label="PostgreSQL"
          tooltip="EBS-Volume des PostgreSQL Primary (gp3, 300 Gi). Enthält alle Datenbanken (storage, keycloak, chat, session) inkl. Bilder als BLOBs, WAL-Logs und Indizes."
          value={data.pgDiskUsage}
          total={data.pgDiskTotal}
          unit="bytes"
          color="red"
        />
        <Tooltip label="Anzahl laufender Pods im default-Namespace. Beinhaltet alle Applikations-Services (storage, keycloak, payment, mailer, etc.) sowie Infrastruktur-Pods (etcd, postgres, ollama)." multiline w={280} withArrow>
          <Center>
            <Stack align="center" gap={4}>
              <RingProgress
                size={100}
                thickness={10}
                roundCaps
                sections={[{ value: Math.min(data.podCount * 2, 100), color: 'teal' }]}
                label={<Text ta="center" fw={600} size="sm">{data.podCount}</Text>}
              />
              <Text size="sm" fw={500}>Pods</Text>
              <Text size="xs" c="dimmed">Running</Text>
            </Stack>
          </Center>
        </Tooltip>
        <Tooltip label="PostgreSQL-Transaktionen pro Sekunde (storage + keycloak). Zeigt die aktuelle Last auf der Datenbank. Darunter: logische Gesamtgröße aller Datenbanken laut pg_database_size." multiline w={280} withArrow>
          <Center>
            <Stack align="center" gap={4}>
              <RingProgress
                size={100}
                thickness={10}
                roundCaps
                sections={[{ value: Math.min(data.dbTransactionsPerSec * 10, 100), color: 'yellow' }]}
                label={<Text ta="center" fw={600} size="sm">{data.dbTransactionsPerSec.toFixed(1)}</Text>}
              />
              <Text size="sm" fw={500}>DB tx/s</Text>
              <Text size="xs" c="dimmed">{formatBytes(data.dbSizeBytes)} total</Text>
            </Stack>
          </Center>
        </Tooltip>
      </SimpleGrid>
    </Paper>
  );
}
